import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useWishlist } from "../context/WishlistContext";
import { useLanguage } from "../context/LanguageContext";
import { motion } from "framer-motion";

export default function ProductCard({ product, index = 0 }) {
  const { toggleItem, isInWishlist } = useWishlist();
  const { t } = useLanguage();
  const wishlisted = isInWishlist(product.id);
  const hasDiscount = product.discount > 0;
  const soldOut = product.in_stock === false || product.stock_quantity === 0;
  const discountedPrice = hasDiscount
    ? product.price * (1 - product.discount / 100)
    : product.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative">
        <Link to={`/product/${product.id}`}>
          <div className="product-image-container aspect-[3/4] bg-[#F4F0EB] relative overflow-hidden">
            <img
              src={product.images?.[0] || "https://via.placeholder.com/400x533"}
              alt={product.name}
              className={`w-full h-full object-cover transition-opacity ${soldOut ? "opacity-70" : ""}`}
              loading="lazy"
            />
            {soldOut && (
              <div
                className="absolute inset-x-0 bottom-0 bg-[#1A1A1A]/85 backdrop-blur-sm text-center py-2.5"
                data-testid={`sold-out-badge-${product.id}`}
              >
                <span className="text-white text-[10px] tracking-[0.3em] uppercase font-medium">
                  Sold Out
                </span>
              </div>
            )}
          </div>
        </Link>
        {hasDiscount && !soldOut && (
          <span
            className="absolute top-4 left-4 bg-[#8B2020] text-white text-[10px] tracking-wider font-semibold px-2.5 py-1"
            data-testid={`discount-badge-${product.id}`}
          >
            -{product.discount}%
          </span>
        )}
        <button
          onClick={(e) => {
            e.preventDefault();
            toggleItem(product);
          }}
          className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm hover:bg-white transition-all opacity-0 group-hover:opacity-100"
          data-testid={`wishlist-toggle-${product.id}`}
        >
          <Heart
            size={16}
            strokeWidth={1.5}
            className={wishlisted ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#1A1A1A]"}
          />
        </button>
      </div>
      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] mb-1">
          {product.brand}
        </p>
        <Link to={`/product/${product.id}`}>
          <h3 className="text-sm tracking-wide hover:opacity-70 transition-opacity" style={{ fontFamily: "'Playfair Display', serif" }}>
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-1.5">
          {hasDiscount && (
            <p className="text-xs tracking-wider line-through text-[#9CA3AF]" style={{ fontFamily: "'Lato', sans-serif" }}>
              &euro;{product.price?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
            </p>
          )}
          <p className={`text-xs tracking-wider ${hasDiscount ? "text-[#8B2020] font-semibold" : ""}`} style={{ fontFamily: "'Lato', sans-serif" }} data-testid={`product-price-${product.id}`}>
            &euro;{discountedPrice?.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
